// File: frontend/src/components/marketing/visuals/MarketingRouterVisual.js
//
// Marketing recreation of a PesaFii router detail card (inspired by the Routers
// page fields): status, active users, uptime, CPU, memory, data usage, last seen.
// Animated resource bars. Fictional data; no API calls.

import React from "react";
import { Box, Paper, Typography, Stack, LinearProgress, useTheme, alpha } from "@mui/material";
import RouterIcon from "@mui/icons-material/Router";
import { MarketingStatusBadge, MarketingMetricCard, SampleNote } from "./primitives";
import { useInViewOnce } from "../motion";
import { DEMO_LOCATIONS, DEMO_DEFAULT_LOCATION_ID } from "../../../data/marketingDemoData";

function ResourceBar({ label, value, color, active }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11.5, color: "text.primary", fontWeight: 700 }}>{value}%</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={active ? value : 0}
        sx={{ height: 6, borderRadius: 99, transition: "none",
          "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 99, transition: "transform 1s cubic-bezier(0.22,1,0.36,1)" } }} />
    </Box>
  );
}

/**
 * @param {object} [location] One of DEMO_LOCATIONS. Defaults to the location the
 *   network diagram starts on, so the product tour can use this card with no
 *   props at all.
 */
export default function MarketingRouterVisual({ location }) {
  const theme = useTheme();
  const [ref, inView] = useInViewOnce();
  const green = theme.palette.primary.main;
  const r = location || DEMO_LOCATIONS.find((l) => l.id === DEMO_DEFAULT_LOCATION_ID) || DEMO_LOCATIONS[0];

  return (
    <Box ref={ref}>
      <Paper sx={{ p: { xs: 2, sm: 2.25 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, flexShrink: 0, bgcolor: alpha(green, 0.14), color: green, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <RouterIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 15, fontWeight: 800, color: "text.primary" }}>{r.name}</Typography>
              <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>Last seen {r.lastSeen}</Typography>
            </Box>
          </Stack>
          <MarketingStatusBadge status={r.online ? "online" : "offline"} size="md" />
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 2 }}>
          <MarketingMetricCard key={`u-${r.id}`} label="Active users" value={r.users} active={inView} color={theme.palette.info.main} />
          <Paper sx={{ p: 1.5, borderRadius: 2.5, border: "1px solid", borderColor: "divider", position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, bgcolor: theme.palette.success.main }} />
            <Typography sx={{ fontSize: 10.5, color: "text.secondary", fontWeight: 600, mb: 0.75 }}>Uptime</Typography>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: "text.primary" }}>{r.uptime}</Typography>
          </Paper>
          <MarketingMetricCard key={`d-${r.id}`} label="Data used" value={r.dataUsedGb} suffix=" GB" decimals={1} active={inView} color={theme.palette.warning.main} />
        </Box>

        <Stack spacing={1.5}>
          <ResourceBar label="CPU" value={r.cpu} color={green} active={inView} />
          <ResourceBar label="Memory" value={r.memory} color={theme.palette.info.main} active={inView} />
        </Stack>
      </Paper>
      <SampleNote />
    </Box>
  );
}
